import React, { createContext, useContext, useState, useEffect } from "react";
import { GlobalStyles } from "@mui/material";
import { ThemeProvider as MuiThemeProvider } from "@mui/material/styles";
import {
  auth,
  onAuthStateChange,
  signInWithGoogle,
  signOutUser,
} from "../services/firebase";
import { getRedirectResult } from "firebase/auth";
import { isPersonalEmail } from "../utils/emailDomains";
import { isConsoleHost } from "../utils";
import { identifyUser, resetUser, capture, setGroup } from "../services/posthog";
import { createAppTheme, WEB_SANS_FONT, PLUGIN_SANS_FONT } from "../design/theme";
import { analytics } from "../services/analytics";
import { createDesignTokenStyles } from "../design/designSystem";

/**
 * Consolidated User Context
 * Combines authentication and theme functionality
 */

// Theme creation now uses the unified theme.js functions

// Create context
const UserContext = createContext();

// User provider component that combines auth and theme
export function UserProvider({ children }) {
  // Auth state
  const [currentUser, setCurrentUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  // Theme state - default to light mode (better for consulting aesthetic)
  const [isDarkMode, setIsDarkMode] = useState(() => {
    if (typeof window === "undefined") {
      return false;
    }
    try {
      const saved = window.localStorage.getItem("isDarkMode");
      if (saved !== null) {
        return JSON.parse(saved);
      }
      window.localStorage.setItem("isDarkMode", "false");
    } catch (error) {
      console.warn("Unable to read theme preference from localStorage", error);
    }
    return false;
  });

  // Initialize auth
  useEffect(() => {
    // Check for redirect result on app initialization
    const checkRedirectResult = async () => {
      try {
        const result = await getRedirectResult(auth);
        if (result) {
          console.log("Redirect sign-in successful:", result.user);
        }
      } catch (error) {
        console.error("Error getting redirect result:", error);
      }
    };

    checkRedirectResult();

    // Subscribe to auth state changes
    const unsubscribe = onAuthStateChange((user) => {
      const isNowSignedIn = !!user;
      const wasSignedIn = !!(auth.currentUser && auth.currentUser.uid);

      setCurrentUser(user);
      setAuthLoading(false);

      // Track authentication events
      if (!wasSignedIn && isNowSignedIn) {
        // User just signed in
        const providerId = user.providerData?.[0]?.providerId || 'unknown';
        const signInMethod = providerId === 'password'
          ? 'email'
          : providerId === 'microsoft.com'
            ? 'microsoft'
            : providerId === 'google.com'
              ? 'google'
              : providerId;
        analytics.trackAuth("sign_in", signInMethod);
        analytics.setUserId(user.uid);

        // Set comprehensive user properties
        analytics.setUserProperties({
          user_type: "authenticated",
          sign_in_method: signInMethod,
          first_login_date:
            user.metadata?.creationTime || new Date().toISOString(),
          last_login_date:
            user.metadata?.lastSignInTime || new Date().toISOString(),
        });

        // PostHog: identify user
        identifyUser(user);
        capture('signed_in', { method: signInMethod });
      } else if (wasSignedIn && !isNowSignedIn) {
        // User just signed out
        analytics.trackAuth("sign_out", "google");
        analytics.setUserId(null);
        capture('signed_out');
        resetUser();

        // Clear user properties on sign out
        analytics.setUserProperties({
          user_type: "anonymous",
          sign_in_method: "none",
        });
      }
    });

    // Cleanup subscription
    return () => unsubscribe();
  }, []);

  // Handle theme changes
  const toggleDarkMode = () => {
    setIsDarkMode((prev) => {
      const newValue = !prev;
      localStorage.setItem("isDarkMode", JSON.stringify(newValue));

      // Track theme change
      analytics.trackSettings(
        "change_theme",
        "theme_mode",
        newValue ? "dark" : "light",
      );

      return newValue;
    });
  };

  // Marketing routes always use light mode; the plugin (/chat) and the
  // console (/settings, or any route on the console subdomain) respect the
  // user's dark-mode preference. Surface picks the palette: 'plugin' is the
  // PPT-neutral register, 'console' is the landing-page rustic register.
  // Initial detection is path-based (correct on full page loads); client-side
  // navigation into the console is covered by setSurfaceOverride, which
  // SettingsScreen sets on mount (the provider sits above the Router, so it
  // can't read route changes itself).
  const [surfaceOverride, setSurfaceOverride] = useState(null);
  const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
  const isPlugin = pathname.startsWith('/chat');
  const isConsolePath = !isPlugin && (isConsoleHost() || pathname.startsWith('/settings') || pathname.startsWith('/admin'));
  const surface = surfaceOverride || (isPlugin ? 'plugin' : isConsolePath ? 'console' : 'web');
  const effectiveDarkMode = (surface === 'plugin' || surface === 'console') ? isDarkMode : false;

  // Create theme using unified theme.js function. Surface picks the font
  // stack: web → Inter (brand-consistent across devices), plugin → Fluent
  // UI system stack (matches PowerPoint's native UI inside the taskpane).
  const theme = createAppTheme(effectiveDarkMode ? "dark" : "light", surface);

  // Sync the global `--font-family-sans` CSS variable with the active
  // surface so plain HTML elements (anything not styled via MUI Typography)
  // also pick up the right font. Body, raw <p>/<h*>, code blocks, etc.
  // all resolve through this variable in global.css.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    root.dataset.surface = surface;
    root.style.setProperty(
      '--font-family-sans',
      isPlugin ? PLUGIN_SANS_FONT : WEB_SANS_FONT
    );
  }, [isPlugin, surface]);

  // Email verification removed. The product is Microsoft-first (SSO + the
  // Microsoft dialog), and the SMTP verification path was unreliable, so we
  // no longer gate access on email verification. Kept as a constant `false`
  // so existing consumers (useUser/useAuth) don't break.
  const needsEmailVerification = false;

  // Context value
  const value = {
    // Auth
    currentUser,
    authLoading,
    signIn: signInWithGoogle,
    signOut: signOutUser,
    isAuthenticated: !!currentUser,
    needsEmailVerification,
    isPersonalEmail: !!currentUser && isPersonalEmail(currentUser.email),

    // Theme
    isDarkMode: effectiveDarkMode,
    toggleDarkMode,
    theme,
    surface,
    setSurfaceOverride,
  };

  return (
    <UserContext.Provider value={value}>
      <MuiThemeProvider theme={theme}>
        <GlobalStyles
          styles={(muiTheme) => createDesignTokenStyles(muiTheme)}
        />
        {!authLoading && children}
      </MuiThemeProvider>
    </UserContext.Provider>
  );
}

// Custom hook to use user context (combines auth and theme)
export function useUser() {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
}

// Backward compatibility hooks
export function useAuth() {
  const { currentUser, authLoading, signIn, signOut, isAuthenticated, needsEmailVerification, isPersonalEmail: isPersonal } =
    useUser();
  return {
    currentUser,
    loading: authLoading,
    signIn,
    signOut,
    isAuthenticated,
    needsEmailVerification,
    isPersonalEmail: isPersonal,
  };
}

export function useTheme() {
  const { isDarkMode, toggleDarkMode, theme } = useUser();
  return { isDarkMode, toggleDarkMode, theme };
}
